import { useRef } from 'react';
import { useIntersection } from 'react-use';

import { Transition } from '@headlessui/react';
import clsx from 'clsx';

// Utils
import { useProfileHasAccessPage } from '@/utils/hooks/acl/useProfileHasAccessPage';
import useDisplayFirstStatsPanel from '@/utils/hooks/firstStats/useDisplayFirstStatsPanel';

// Components
import SelectedProfiles from '@/components/composite/SelectedProfiles';
import { PopoverList } from '@/components/layouts/LayoutPlatform/components/PageHeaderRecap/PopoverList';

export type PageHeaderChildrenProps = {
    isSticky?: boolean;
};

export type DropdownLinksType = {
    href: string;
    label: React.ReactNode;
    onClick?: (e: React.MouseEvent) => void;
    restricted?: boolean;
}[];

type PageHeaderRecapProps = {
    title: React.ReactNode;
    hideStickyHeader?: boolean;
    children?: (args: PageHeaderChildrenProps) => React.ReactNode;
    dropdownLinks?: DropdownLinksType;
};

export function PageHeaderRecap({
    title,
    children,
    hideStickyHeader = false,
    dropdownLinks
}: PageHeaderRecapProps) {
    const headerRef = useRef<HTMLDivElement | null>(null);
    const headerIntersection = useIntersection(headerRef, {
        threshold: 0,
        root: document.querySelector('.main-content-scroll'),
        rootMargin: '100px 0px 0px 0px'
    });

    const {
        activeGroupNoAccess,
        activeMultiSelectionProfilesNoAccess,
        activeSocialProfileNoAccess
    } = useProfileHasAccessPage();

    const { hideFirstStatsEmptyState } = useDisplayFirstStatsPanel();

    const displayChildren =
        children &&
        !activeGroupNoAccess &&
        !activeMultiSelectionProfilesNoAccess &&
        !activeSocialProfileNoAccess &&
        hideFirstStatsEmptyState;

    const isSticky = Boolean(headerRef.current && !headerIntersection?.isIntersecting);

    return (
        <>
            {!hideStickyHeader ? (
                <div
                    className="sticky top-0 z-50 hidden sm:-mt-8 sm:block"
                    data-testid="sticky-page-header-recap">
                    <Transition
                        show={isSticky}
                        enter="transition-all ease-in-out duration-500 transform"
                        enterFrom="-translate-y-full"
                        enterTo="translate-y-0"
                        leave="transition ease-in-out duration-300 transform"
                        leaveFrom="translate-y-0"
                        leaveTo="-translate-y-full">
                        <div className="absolute -left-5 -top-5 z-20 sm:-left-[2rem] sm:-right-[2rem] sm:-top-0 sm:w-auto">
                            <div
                                className={clsx(
                                    'flex w-full items-center justify-between rounded-t-2xl border-b px-4 py-2.5',
                                    'border-icoGray-300 dark:border-icoDarkMode-beton',
                                    'bg-white dark:bg-icoDarkMode-deepBlack'
                                )}>
                                <div className="flex flex-1 items-center gap-5">
                                    <div className="flex items-center gap-2">
                                        <PopoverList links={dropdownLinks} isSticky={true} />
                                        <p className="font-GBold">{title}</p>
                                    </div>
                                    <div className="min-w-44 flex-1">
                                        <SelectedProfiles itemsSize="md" />
                                    </div>
                                </div>
                                {displayChildren ? <div>{children({ isSticky: true })}</div> : null}
                            </div>
                        </div>
                    </Transition>
                </div>
            ) : (
                // This block is used to add a margin top for the main header
                <div className="-mt-8" />
            )}
            <div ref={headerRef} className="relative z-20 mb-8 sm:my-8">
                <div
                    className={clsx('grid grid-rows-2 gap-4', {
                        'grid-cols-[auto,1fr,auto] @[1144px]/wrapper-platform-content:grid-rows-1 @[1144px]/wrapper-platform-content:gap-8':
                            displayChildren,
                        'grid-cols-[auto,1fr] @[500px]/wrapper-platform-content:grid-rows-1 @[500px]/wrapper-platform-content:gap-8':
                            !displayChildren
                    })}>
                    <div className="z-20 flex flex-wrap items-center justify-between gap-3">
                        <PopoverList links={dropdownLinks} />
                        <h2 className="font-GBold text-2xl @[500px]/wrapper-platform-content:text-3.5xl">
                            {title}
                        </h2>
                    </div>
                    <div
                        className={clsx('col-start-1 row-start-2', {
                            'col-span-3 @[1144px]/wrapper-platform-content:col-span-1 @[1144px]/wrapper-platform-content:col-start-2 @[1144px]/wrapper-platform-content:row-start-1':
                                displayChildren,
                            'col-span-2 @[500px]/wrapper-platform-content:col-span-1 @[500px]/wrapper-platform-content:col-start-2 @[500px]/wrapper-platform-content:row-start-1':
                                !displayChildren
                        })}>
                        <SelectedProfiles />
                    </div>
                    {displayChildren ? (
                        <div className="col-start-3 row-start-1 items-center">
                            {children({ isSticky: false })}
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
}
